import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Stamp, Upload, X } from 'lucide-react';
import type { WatermarkOptions, WatermarkPosition } from '../types';
import { WATERMARK_POSITIONS } from '../constants';
import { supabase } from '../services/supabase';
import {
  BrandProfilePicker,
  MediaBrandManager,
  fetchBrandProfiles,
  createBrandProfile,
  updateBrandProfile,
  deleteBrandProfile,
  uploadBrandProfileLogo,
} from '@rehub/branded-viewer';
import type { MediaBrandProfile } from '@rehub/branded-viewer';

const LOGO_POSITION_MAP: Record<string, WatermarkPosition> = {
  'top-left': 'top-left',
  'top-right': 'top-right',
};

interface WatermarkControlsProps {
  options: WatermarkOptions;
  onChange: (options: WatermarkOptions) => void;
  userId?: string | null;
}

const WatermarkControls: React.FC<WatermarkControlsProps> = ({ options, onChange, userId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [brands, setBrands] = useState<MediaBrandProfile[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [showBrandManager, setShowBrandManager] = useState(false);
  const [loadingBrandLogo, setLoadingBrandLogo] = useState(false);
  const brandsFetched = useRef(false);

  const loadBrands = useCallback(async () => {
    if (!userId) return [];
    setBrandsLoading(true);
    try {
      const data = await fetchBrandProfiles(supabase, userId);
      setBrands(data);
      return data;
    } finally {
      setBrandsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && options.enabled && !brandsFetched.current) {
      brandsFetched.current = true;
      loadBrands();
    }
  }, [userId, options.enabled, loadBrands]);

  const handleBrandSelect = useCallback(async (brand: MediaBrandProfile) => {
    if (!brand.logo_url) return;
    setLoadingBrandLogo(true);
    try {
      const res = await fetch(brand.logo_url);
      const blob = await res.blob();
      const ext = brand.logo_url.split('.').pop()?.split('?')[0] || 'png';
      const file = new File([blob], `${brand.name}.${ext}`, { type: blob.type });
      if (options.logoPreviewUrl) URL.revokeObjectURL(options.logoPreviewUrl);
      const previewUrl = URL.createObjectURL(blob);
      onChange({
        ...options,
        enabled: true,
        logoFile: file,
        logoPreviewUrl: previewUrl,
        position: LOGO_POSITION_MAP[brand.logo_position] || options.position,
      });
    } catch (err) {
      console.error('Failed to load brand logo:', err);
    } finally {
      setLoadingBrandLogo(false);
    }
  }, [options, onChange]);

  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (options.logoPreviewUrl) {
      URL.revokeObjectURL(options.logoPreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    onChange({ ...options, logoFile: file, logoPreviewUrl: previewUrl });
    e.target.value = '';
  }, [options, onChange]);

  const removeLogo = useCallback(() => {
    if (options.logoPreviewUrl) {
      URL.revokeObjectURL(options.logoPreviewUrl);
    }
    onChange({ ...options, logoFile: null, logoPreviewUrl: null });
  }, [options, onChange]);

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-cyan-900/30 border border-cyan-500/20 flex items-center justify-center">
          <Stamp size={16} className="text-cyan-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-200">Logo / Watermark</h3>
        <label className="ml-auto flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-slate-400">{options.enabled ? 'On' : 'Off'}</span>
          <button
            onClick={() => onChange({ ...options, enabled: !options.enabled })}
            className={`relative w-10 h-5 rounded-full transition-colors ${options.enabled ? 'bg-cyan-600' : 'bg-slate-700'}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${options.enabled ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </label>
      </div>

      {options.enabled && (
        <div className="space-y-4 animate-fade-in">
          {/* Logo Upload */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
            {options.logoPreviewUrl ? (
              <div className="flex items-center gap-3 p-3 bg-slate-800/80 border border-slate-700 rounded-xl">
                <img src={options.logoPreviewUrl} alt="Logo" className="w-12 h-12 object-contain rounded bg-slate-900 p-1" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-300 truncate">{options.logoFile?.name}</p>
                  <p className="text-[10px] text-slate-500">Click to change</p>
                </div>
                <button
                  onClick={removeLogo}
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-900/30 text-slate-400 hover:text-red-400 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                {userId && (
                  <BrandProfilePicker
                    brands={brands}
                    loading={brandsLoading || loadingBrandLogo}
                    onSelect={handleBrandSelect}
                    onManage={() => setShowBrandManager(true)}
                    accentColor="#06b6d4"
                  />
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-slate-700 hover:border-cyan-500/50 rounded-xl text-sm text-slate-400 hover:text-cyan-400 transition-all"
                >
                  <Upload size={16} />
                  Upload logo (PNG recommended)
                </button>
              </>
            )}
          </div>

          {/* Position */}
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Position</label>
            <div className="flex flex-wrap gap-1.5">
              {WATERMARK_POSITIONS.map((pos) => (
                <button
                  key={pos.value}
                  onClick={() => onChange({ ...options, position: pos.value })}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    options.position === pos.value
                      ? 'bg-cyan-600 text-white border border-cyan-500'
                      : 'bg-slate-800 text-slate-300 border border-slate-700 hover:border-cyan-500/40'
                  }`}
                >
                  {pos.label}
                </button>
              ))}
            </div>
          </div>

          {/* Size Slider */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-slate-400">Size</label>
              <span className="text-xs text-slate-500">{Math.round(options.scale * 100)}%</span>
            </div>
            <input
              type="range"
              min={5}
              max={50}
              value={Math.round(options.scale * 100)}
              onChange={(e) => onChange({ ...options, scale: parseInt(e.target.value) / 100 })}
              className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-cyan-500"
            />
          </div>

          {/* Opacity Slider */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-slate-400">Opacity</label>
              <span className="text-xs text-slate-500">{Math.round(options.opacity * 100)}%</span>
            </div>
            <input
              type="range"
              min={10}
              max={100}
              value={Math.round(options.opacity * 100)}
              onChange={(e) => onChange({ ...options, opacity: parseInt(e.target.value) / 100 })}
              className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-cyan-500"
            />
          </div>

          {/* Margin Slider */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs text-slate-400">Margin from edge</label>
              <span className="text-xs text-slate-500">{options.margin}px</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={options.margin}
              onChange={(e) => onChange({ ...options, margin: parseInt(e.target.value) })}
              className="w-full h-1.5 bg-slate-700 rounded-full appearance-none cursor-pointer accent-cyan-500"
            />
          </div>
        </div>
      )}

      {showBrandManager && userId && (
        <MediaBrandManager
          userId={userId}
          brands={brands}
          loading={brandsLoading}
          onClose={() => setShowBrandManager(false)}
          onLoadBrands={loadBrands}
          onCreateBrand={(input) => createBrandProfile(supabase, userId, input)}
          onUpdateBrand={(id, input) => updateBrandProfile(supabase, userId, id, input)}
          onDeleteBrand={(id) => deleteBrandProfile(supabase, userId, id)}
          onUploadLogo={(file) => uploadBrandProfileLogo(supabase, file, userId)}
          applyMode
          onBrandApplied={(brand) => { handleBrandSelect(brand); setShowBrandManager(false); }}
          accentColor="#06b6d4"
          mode="watermark"
        />
      )}
    </div>
  );
};

export default WatermarkControls;
